import { ComponentType } from 'react';
import { ROLES } from '@tempus/trials-types';

import { PATIENT_WORKSPACE_AUTHORIZED_ROLES } from '~/auth/patientWorkspaceAccess';
import withPageTitle from '~/components/_common/PageTitle';
import paths, { SubPaths } from './paths';
import withLazyLoad from './withLazyLoad';

const ActivationPacket = withLazyLoad(
  () => import(/* webpackChunkName: "activation-packet" */ '~/routes/ActivationPacket'),
);
const AllPatientsPage = withLazyLoad(
  () => import(/* webpackChunkName: "all-patients" */ '~/routes/patientTracker/queues/allPatients'),
);
const MatchReviewPage = withLazyLoad(
  () => import(/* webpackChunkName: "match-review" */ '~/routes/patientTracker/queues/matchReview'),
);
const PatientWorkspacePageGate = withLazyLoad(
  () =>
    import(
      /* webpackChunkName: "patient-workspace" */ '~/routes/PatientWorkspacePage/PatientWorkspacePageGate/PatientWorkspacePageGate'
    ),
);
const CarePathwayPatientPage = withLazyLoad(
  () =>
    import(
      /* webpackChunkName: "care-pathway-patient-redirect" */ '~/routes/PatientWorkspacePage/CarePathwayPatientPage/CarePathwayPatientPage'
    ),
);
const PatientTrackerSitesV2 = withLazyLoad(
  () => import(/* webpackChunkName: "patient-tracker-sites" */ '~/routes/patientTracker/sites'),
);
const PatientTrialEligibilityReviewPage = withLazyLoad(
  () => import(/* webpackChunkName: "patient-trial-eligibility-review" */ '~/routes/patientTrialEligibilityReview'),
);
const SiteDocuments = withLazyLoad(() => import(/* webpackChunkName: "site-documents" */ '~/routes/siteId/documents'));
const TempusNursePage = withLazyLoad(
  () => import(/* webpackChunkName: "tempus-nurse" */ '~/routes/patientTracker/queues/tempusNurse'),
);
const TimeScreeningPage = withLazyLoad(
  () => import(/* webpackChunkName: "time-screening" */ '~/routes/timeScreeningPage'),
);
const TrialDocuments = withLazyLoad(
  () => import(/* webpackChunkName: "trial-documents" */ '~/routes/trialId/documents'),
);
const SiteTrialPage = withLazyLoad(() => import(/* webpackChunkName: "site-trial" */ '~/routes/trialId/siteTrial'));
const TrialsPage = withLazyLoad(() => import(/* webpackChunkName: "trials" */ '~/routes/trialsPage'));
const TrialMaterials = withLazyLoad(
  () => import(/* webpackChunkName: "trial-materials" */ '~/routes/trialsPage/trialMaterials'),
);
const TrialSearch = withLazyLoad(
  () => import(/* webpackChunkName: "trial-search" */ '~/routes/trialsPage/trialSearch'),
);
const NotificationPreferences = withLazyLoad(
  () => import(/* webpackChunkName: "notification-preferences" */ '~/routes/user/notification-preferences'),
);
const MatchingPatientsRoute = withLazyLoad(
  () => import(/* webpackChunkName: "matching-patients" */ '~/routes/timeScreeningPage/matchingPatients'),
);
const MatchingCarePathwayRoute = withLazyLoad(
  () => import(/* webpackChunkName: "matching-care-pathways" */ '~/routes/timeScreeningPage/matchingCarePathways'),
);
const CarePathways = withLazyLoad(
  () => import(/* webpackChunkName: "care-pathways" */ '~/routes/carePathways/CarePathways'),
);
const ProviderReportingPage = withLazyLoad(
  () => import(/* webpackChunkName: "reporting" */ '~/routes/reporting/Reporting'),
);
const ProviderReportingDashboardPage = withLazyLoad(
  () => import(/* webpackChunkName: "reporting-dashboard" */ '~/routes/reporting/ReportingDashboard'),
);
const ScreenedCarePathwaysRoute = withLazyLoad(
  () => import(/* webpackChunkName: "screened-care-pathways" */ '~/routes/timeScreeningPage/screenedCarePathways'),
);
const ScreenedPatientsRoute = withLazyLoad(
  () => import(/* webpackChunkName: "screened-patients" */ '~/routes/timeScreeningPage/screenedPatients'),
);
const AdminPage = withLazyLoad(() => import(/* webpackChunkName: "admin" */ '~/routes/admin/AdminPage'));
const BuilderPage = withLazyLoad(() => import(/* webpackChunkName: "builder" */ '~/routes/builder/BuilderPage'));
const ProjectDetailPage = withLazyLoad(
  () => import(/* webpackChunkName: "builder-project" */ '~/routes/builder/ProjectDetailPage'),
);
const StagesPage = withLazyLoad(() => import(/* webpackChunkName: "builder-stages" */ '~/routes/builder/StagesPage'));
const RulesetEditorPage = withLazyLoad(
  () => import(/* webpackChunkName: "ruleset-editor" */ '~/routes/builder/RulesetEditorPage'),
);
const EvaluationJobsPage = withLazyLoad(
  () => import(/* webpackChunkName: "builder-evaluation-jobs" */ '~/routes/builder/EvaluationJobsPage'),
);
const EvaluationJobDetailPage = withLazyLoad(
  () => import(/* webpackChunkName: "builder-evaluation-job" */ '~/routes/builder/EvaluationJobDetailPage'),
);
const PatientQueryJobDetailPage = withLazyLoad(
  () => import(/* webpackChunkName: "builder-patient-query-job" */ '~/routes/builder/PatientQueryJobDetailPage'),
);
const PathwayPatientsPage = withLazyLoad(
  () =>
    import(
      /* webpackChunkName: "pathway-patients" */ '~/routes/pathwayPatients/PathwayPatientsPage/PathwayPatientsPage'
    ),
);
const ProgramManagementPage = withLazyLoad(
  () => import(/* webpackChunkName: "program-management" */ '~/routes/programManagement'),
);
const ProgramManagementSiteDetailPage = withLazyLoad(
  () => import(/* webpackChunkName: "program-management" */ '~/routes/programManagement/SiteDetail'),
);
const ProgramManagementPathwayDetailPage = withLazyLoad(
  () => import(/* webpackChunkName: "program-management" */ '~/routes/programManagement/PathwayDetail'),
);

export interface RouteConfig {
  title: string;
  path: string;
  component: ComponentType;
  authorizedRoles?: string[];
  showSiteSelection?: boolean;
  children?: RouteConfig[];
}

export const Routes: Record<string, RouteConfig> = {
  trials: {
    title: 'Trials',
    path: paths.TRIALS,
    component: TrialsPage,
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_TRIALS_READ],
    children: [
      {
        title: 'Find Trials',
        path: SubPaths.SEARCH,
        component: withPageTitle(TrialSearch, 'Find Trials'),
        authorizedRoles: [ROLES.T_TRIALS_READ],
      },
      {
        title: 'Materials',
        path: SubPaths.MATERIALS,
        component: withPageTitle(TrialMaterials, 'Materials'),
        authorizedRoles: [ROLES.T_TRIALS_READ],
      },
    ],
  },
  trialDocuments: {
    title: 'Trial Documents',
    path: paths.TRIAL_DOCUMENTS,
    component: withPageTitle(TrialDocuments, 'Trial Documents'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_DOCUMENTS_READ],
  },
  siteTrial: {
    title: 'Site trial',
    path: paths.SITE_TRIAL,
    component: withPageTitle(SiteTrialPage, 'Site trial'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_PATIENT_TRACKER_READ, ROLES.T_PATIENT_TRACKER_READ_INTERNAL],
  },
  siteDocuments: {
    title: 'Site Documents',
    path: paths.SITE_DOCUMENTS,
    component: withPageTitle(SiteDocuments, 'Site Documents'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_DOCUMENTS_READ],
  },
  patientTrackerSites: {
    title: 'Patient Tracker Sites',
    path: paths.PATIENT_TRACKER_SITES,
    component: withPageTitle(PatientTrackerSitesV2, 'Patient Tracker Sites'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_PATIENT_TRACKER_READ_INTERNAL],
  },
  matchReviewPage: {
    title: 'Match Review',
    path: paths.MATCH_REVIEW,
    component: withPageTitle(MatchReviewPage, 'Match Review'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_PATIENT_TRACKER_READ, ROLES.T_PATIENT_TRACKER_READ_INTERNAL],
  },
  allPatientsPage: {
    title: 'All Patients',
    path: paths.ALL_PATIENTS,
    component: withPageTitle(AllPatientsPage, 'All Patients'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_PATIENT_TRACKER_READ, ROLES.T_PATIENT_TRACKER_READ_INTERNAL],
  },
  tempusNursePage: {
    title: 'Tempus Nurse',
    path: paths.TEMPUS_NURSE,
    component: withPageTitle(TempusNursePage, 'Tempus Nurse'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.T_PATIENT_TRACKER_READ_INTERNAL],
  },
  patientWorkspacePage: {
    authorizedRoles: PATIENT_WORKSPACE_AUTHORIZED_ROLES,
    component: withPageTitle(PatientWorkspacePageGate, 'Patient'),
    path: paths.PATIENT_V2,
    title: 'Patient',
  },
  carePathwayPatientPage: {
    title: 'Patient care pathway',
    path: paths.PATIENT_CARE_PATHWAY,
    component: withPageTitle(CarePathwayPatientPage, 'Patient care pathway'),
    authorizedRoles: PATIENT_WORKSPACE_AUTHORIZED_ROLES,
  },
  userNotificationPreferences: {
    title: 'Notification Preferences',
    path: paths.USER_NOTIFICATION_PREFERENCES,
    component: withPageTitle(NotificationPreferences, 'Notification Preferences'),
    showSiteSelection: false,
    authorizedRoles: [ROLES.T_SITE_USER, ROLES.T_SITE_BASE_USER],
  },
  activationPacketSubmission: {
    title: 'Activation Packet Submission',
    path: paths.ACTIVATION_PACKET,
    component: withPageTitle(ActivationPacket, 'Activation Packet'),
    showSiteSelection: false,
  },
  timeScreeningTrials: {
    title: 'TIME Screening',
    path: paths.TIME_SCREENING_TRIALS,
    component: TimeScreeningPage,
    authorizedRoles: [ROLES.T_PATIENT_MATCHER, ROLES.NEXT_PRESCREENING_USER],
    children: [
      {
        title: 'Matching',
        path: SubPaths.MATCHING,
        component: withPageTitle(MatchingPatientsRoute, 'TIME Screening - Matching Patients'),
        authorizedRoles: [ROLES.T_PATIENT_MATCHER, ROLES.NEXT_PRESCREENING_USER],
      },
      {
        title: 'Screened',
        path: SubPaths.SCREENED,
        component: withPageTitle(ScreenedPatientsRoute, 'TIME Screening - Screened Patients'),
        authorizedRoles: [ROLES.T_PATIENT_MATCHER, ROLES.NEXT_PRESCREENING_USER],
      },
    ],
  },
  carePathwayPrescreening: {
    title: 'Care Pathways Pre-screening',
    path: paths.SCREENING_CARE_PATHWAYS,
    component: TimeScreeningPage,
    authorizedRoles: [ROLES.T_PATIENT_MATCHER, ROLES.NEXT_PRESCREENING_USER],
    children: [
      {
        title: 'Matching',
        path: SubPaths.MATCHING,
        component: withPageTitle(MatchingCarePathwayRoute, 'Matching Care Pathways'),
        authorizedRoles: [ROLES.T_PATIENT_MATCHER, ROLES.NEXT_PRESCREENING_USER],
      },
      {
        title: 'Screened',
        path: SubPaths.SCREENED,
        component: withPageTitle(ScreenedCarePathwaysRoute, 'Screened Care Pathways'),
        authorizedRoles: [ROLES.T_PATIENT_MATCHER, ROLES.NEXT_PRESCREENING_USER],
      },
    ],
  },
  patientTrialEligibilityReview: {
    title: 'Patient Trial Eligibility Review',
    path: paths.PATIENT_TRIAL_ELIGIBILITY_REVIEW,
    component: PatientTrialEligibilityReviewPage,
    authorizedRoles: [ROLES.T_PATIENT_MATCHER],
  },
  carePathways: {
    title: 'Care Pathways',
    path: paths.CARE_PATHWAYS,
    component: withPageTitle(CarePathways, 'Care Pathways'),
    showSiteSelection: true,
    authorizedRoles: [ROLES.NEXT_PATIENT_TRACKING_USER, ROLES.NEXT_PATIENT_TRACKING_USER_INTERNAL],
  },
  pathwayPatients: {
    title: 'Care Pathway Queue',
    path: paths.CARE_PATHWAY_QUEUE,
    component: withPageTitle(PathwayPatientsPage, 'Care Pathway Queue'),
    authorizedRoles: [ROLES.NEXT_PATIENT_TRACKING_USER, ROLES.NEXT_PATIENT_TRACKING_USER_INTERNAL],
  },
  admin: {
    title: 'Admin',
    path: paths.ADMIN,
    component: withPageTitle(AdminPage, 'Admin'),
  },
  builder: {
    title: 'Builder',
    path: paths.BUILDER,
    component: withPageTitle(BuilderPage, 'Builder'),
  },
  builderProject: {
    title: 'Project Detail',
    path: paths.BUILDER_PROJECT,
    component: withPageTitle(ProjectDetailPage, 'Project Detail'),
  },
  builderProjectStages: {
    title: 'Stages',
    path: paths.BUILDER_PROJECT_STAGES,
    component: withPageTitle(StagesPage, 'Stages'),
  },
  builderRuleset: {
    title: 'Ruleset Editor',
    path: paths.BUILDER_RULESET,
    component: withPageTitle(RulesetEditorPage, 'Ruleset Editor'),
  },
  builderRulesetEvaluationJobs: {
    title: 'Evaluation Jobs',
    path: paths.BUILDER_RULESET_EVALUATION_JOBS,
    component: withPageTitle(EvaluationJobsPage, 'Evaluation Jobs'),
  },
  builderEvaluationJobDetail: {
    title: 'Evaluation Job',
    path: paths.BUILDER_EVALUATION_JOB_DETAIL,
    component: withPageTitle(EvaluationJobDetailPage, 'Evaluation Job'),
  },
  builderPatientQueryJobDetail: {
    title: 'Patient Query Job',
    path: paths.BUILDER_PATIENT_QUERY_JOB_DETAIL,
    component: withPageTitle(PatientQueryJobDetailPage, 'Patient Query Job'),
  },
  providerReporting: {
    title: 'Reporting',
    path: paths.PROVIDER_REPORTING,
    component: withPageTitle(ProviderReportingPage, 'Reporting'),
  },
  providerReportingDashboard: {
    title: 'Reporting Dashboard',
    path: paths.REPORTING_DASHBOARD,
    component: withPageTitle(ProviderReportingDashboardPage, 'Reporting Dashboard'),
  },
  programManagement: {
    title: 'Program Management',
    path: paths.PROGRAM_MANAGEMENT,
    component: withPageTitle(ProgramManagementPage, 'Program Management'),
  },
  programManagementSite: {
    title: 'Program Management Site',
    path: paths.PROGRAM_MANAGEMENT_SITE,
    component: withPageTitle(ProgramManagementSiteDetailPage, 'Program Management Site'),
  },
  programManagementPathway: {
    title: 'Program Management Pathway',
    path: paths.PROGRAM_MANAGEMENT_PATHWAY,
    component: withPageTitle(ProgramManagementPathwayDetailPage, 'Program Management Pathway'),
  },
};
